import {createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {usePaginate} from "../PaginateProvider";
import {ProposalsPromotersService} from "../../services/Proposals/ProposalsPromotersService";

interface ContextProps {
    proposalsPromoters: any[],
    setProposalsPromoters: Dispatch<SetStateAction<any[]>>,
    isLoading: boolean,
    error: string,
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    handleList: () => Promise<any>,
    handleGet: (id?: number) => Promise<any>,
    handleSave: (payload: any) => Promise<any>,
    handleInative: (id: number | null) => Promise<any>,
}

export const ProposalsPromotersContext = createContext<ContextProps>({} as ContextProps);

interface ProposalsPromotersProviderProps {
    children: ReactNode
}

export const ProposalsPromotersProvider = ({children}: ProposalsPromotersProviderProps) => {
    const [proposalsPromoters, setProposalsPromoters] = useState<any[]>([]);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [params, setParams] = useState<any>({page: 1});

    const {setPagesPaginate} = usePaginate();
    const location = useLocation();
    const service = new ProposalsPromotersService();

    const handleList = useCallback(async () => {
        if (!location.pathname.includes('/proposal-track/promoters/list')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsPromoters(_Response.data);
            setPagesPaginate(_Response.pages);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    }, [location.pathname, params]);

    const handleGet = async (id?: number) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.get({id});

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setProposalsPromoters(_Response.data);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    };


    const handleSave = async (payload: any) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.save(payload);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }

    const handleInative = async (id: number | null) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.delete(id);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }

    useEffect(() => {
        handleList().then();
    }, [handleList]);

    return (
        <ProposalsPromotersContext.Provider value={{
            proposalsPromoters,
            setProposalsPromoters,
            error,
            params,
            setParams,
            handleList,
            handleGet,
            isLoading,
            handleInative,
            handleSave,
        }}>
            {children}
        </ProposalsPromotersContext.Provider>
    )
}

export const useProposalsPromoters = () => useContext(ProposalsPromotersContext);
